import React from 'react';
import { Circle, Defs, G, Line, LinearGradient, Polygon, Rect, Stop, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Duct friction loss — long duct with pressure-gradient shading, two gauges, ΔP arrow. */
export function DiagramDuctFrictionLoss() {
  const x = 30, y = 110, w = 290, h = 55;
  const cy = y + h / 2;

  // Pressure gradient — fake by overlaying graduated vertical strips
  const strips = 14;

  return (
    <DiagramCanvas>
      {/* Duct body */}
      <Rect x={x} y={y} width={w} height={h} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.5" />
      {/* Pressure gradient strips (high pressure left → low right) */}
      {Array.from({ length: strips }).map((_, i) => {
        const sw = w / strips;
        const opacity = 0.32 * (1 - i / strips);
        return (
          <Rect key={i} x={x + i * sw} y={y + 1} width={sw} height={h - 2}
            fill={DT.varColor} opacity={opacity} />
        );
      })}

      {/* Flow arrows in duct */}
      {[0.18, 0.5, 0.82].map((t, i) => {
        const ax = x + w * t;
        return (
          <G key={i}>
            <Line x1={ax - 16} y1={cy} x2={ax + 12} y2={cy} stroke={DT.arrow} strokeWidth="1.5" />
            <Polygon points={`${ax + 14},${cy} ${ax + 8},${cy - 4} ${ax + 8},${cy + 4}`} fill={DT.arrow} />
          </G>
        );
      })}

      {/* Pressure gauges P1 and P2 */}
      {[
        { px: x + 30, label: 'P₁', large: true },
        { px: x + w - 30, label: 'P₂', large: false },
      ].map((g, i) => (
        <G key={i}>
          <Line x1={g.px} y1={y} x2={g.px} y2={y - 18} stroke={DT.leader} strokeWidth="1.2" />
          <Circle cx={g.px} cy={y - 30} r={11} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.2" />
          {/* Needle */}
          <Line x1={g.px} y1={y - 30}
            x2={g.px + (g.large ? -6 : 5)}
            y2={y - 30 + (g.large ? -6 : -3)}
            stroke={DT.varColor} strokeWidth="1.5" />
          <Circle cx={g.px} cy={y - 30} r={1.5} fill={DT.varColor} />
          <SvgText x={g.px} y={y - 44} fill={DT.varColor} fontSize="9"
            fontFamily={DT.mono} textAnchor="middle" fontWeight="700">{g.label}</SvgText>
        </G>
      ))}

      {/* Length L dimension (below) */}
      <Line x1={x} y1={y + h + 14} x2={x + w} y2={y + h + 14} stroke={DT.dim} strokeWidth="1" />
      <Line x1={x} y1={y + h + 10} x2={x} y2={y + h + 18} stroke={DT.dim} strokeWidth="1" />
      <Line x1={x + w} y1={y + h + 10} x2={x + w} y2={y + h + 18} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={x + w / 2} y={y + h + 25} fill={DT.dim} fontSize="9"
        fontFamily={DT.mono} textAnchor="middle">L (ft)</SvgText>

      {/* Diameter arrow on duct */}
      <Line x1={x + w / 2} y1={y} x2={x + w / 2} y2={y + h} stroke={DT.dim} strokeWidth="1" strokeDasharray="3,2" />
      <SvgText x={x + w / 2 + 4} y={cy + 3} fill={DT.dim} fontSize="8" fontFamily={DT.mono}>D</SvgText>

      {/* ΔP indicator (between gauges, above) */}
      <Line x1={x + 30} y1={y - 50} x2={x + w - 30} y2={y - 50} stroke={DT.varColor} strokeWidth="1" />
      <Polygon points={`${x + 30},${y - 50} ${x + 36},${y - 53} ${x + 36},${y - 47}`} fill={DT.varColor} />
      <Polygon points={`${x + w - 30},${y - 50} ${x + w - 36},${y - 53} ${x + w - 36},${y - 47}`} fill={DT.varColor} />
      <SvgText x={x + w / 2} y={y - 54} fill={DT.varColor} fontSize="10"
        fontFamily={DT.mono} textAnchor="middle" fontWeight="700">ΔP</SvgText>

      {/* Labels */}
      <LeaderLine x1={x + 80} y1={y + h} x2={50} y2={210} varName="ΔP/100" desc="Friction rate" anchor="start" />
      <LeaderLine x1={x + w - 60} y1={y + h} x2={310} y2={210} varName="D" desc="Duct dia. (in)" anchor="end" />

      <FormulaTag formula="ΔP_total = (ΔP/100) · L / 100   (in. w.g.)" />
    </DiagramCanvas>
  );
}
